import { PlayerProjectionFragment, TeamInfoWithPlayersFragment } from '@/api/events/query.generated';
import { PlayerWithTeam, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { checkIfRegularProjection } from '@/feature/lobby/utils/lineupsUtils';
import { League, PositionFilters } from '@/types/api.generated';

import { localeCompareCollator } from './collator';
import { getNonRegularProjections } from './getProjectionsTypes';
import { sortEventsByDate } from './sortEvents';

export const mapPlayers = (team?: TeamInfoWithPlayersFragment, league?: League): PlayerWithTeam[] => {
    if (!team || !league) {
        return [];
    }
    const { players, ...teamWithoutPlayers } = team;

    return players?.map(player => ({ ...player, team: teamWithoutPlayers, league, sport: team.sport })) ?? [];
};

export const sortPlayersByName = (firstPlayer: PlayerWithTeam, secondPlayer?: PlayerWithTeam) => {
    const firstPlayerLastName = firstPlayer.lastName;
    const secondPlayerLastName = secondPlayer?.lastName;

    return localeCompareCollator.compare(firstPlayerLastName, secondPlayerLastName ?? '');
};

const getFantasyPointProjectionValue = (projections: PlayerProjectionFragment[]) => {
    const projection = projections.find(item => {
        return item.name.toLowerCase().replace(/\s+/gi, '') === 'fantasypts';
    });

    if (!projection) {
        return 0;
    }

    return projection.value;
};

const sortPlayersByPosition = (
    firstPlayer: PlayerWithTeam,
    secondPlayer?: PlayerWithTeam,
    positionFilters?: PositionFilters[]
) => {
    if (
        firstPlayer.league === League.Simulation ||
        firstPlayer.league !== secondPlayer?.league ||
        !firstPlayer.position ||
        !secondPlayer?.position ||
        !positionFilters
    ) {
        return 0;
    }

    const firstPlayerPositionIndex = positionFilters.findIndex(filter => filter.position === firstPlayer.position);
    const secondPlayerPositionIndex = positionFilters.findIndex(filter => filter.position === secondPlayer.position);

    return firstPlayerPositionIndex - secondPlayerPositionIndex || 0;
};

export const sortPlayersByFantasyPoints = (firstPlayer: PlayerWithTeam, secondPlayer?: PlayerWithTeam) => {
    const firstPlayerFantasyPts = getFantasyPointProjectionValue(firstPlayer?.projections || []) ?? 0;
    const secondPlayerFantasyPts = getFantasyPointProjectionValue(secondPlayer?.projections || []) ?? 0;

    return secondPlayerFantasyPts - firstPlayerFantasyPts;
};

export const sortPlayersByProjectionValue = (
    firstPlayer: PlayerWithTeam,
    secondPlayer?: PlayerWithTeam,
    playerProjectionFilter?: string,
    shouldSortAscending?: boolean
) => {
    const firstPlayerProjectionValue =
        firstPlayer.projections
            .filter(p => p.key === playerProjectionFilter)
            .find(p => checkIfRegularProjection(p.type))?.value ?? 0;
    const secondPlayerProjectionValue =
        secondPlayer?.projections
            .filter(p => p.key === playerProjectionFilter)
            .find(p => checkIfRegularProjection(p.type))?.value ?? 0;

    return shouldSortAscending
        ? firstPlayerProjectionValue - secondPlayerProjectionValue
        : secondPlayerProjectionValue - firstPlayerProjectionValue;
};

export const sortPlayersByEventStartDate = (
    firstPlayer: PlayerWithTeam & { eventStartTime?: string },
    secondPlayer: PlayerWithTeam & { eventStartTime?: string }
) => {
    const firstEventDate = firstPlayer?.eventStartTime;
    const secondEventDate = secondPlayer?.eventStartTime;

    if (!firstEventDate || !secondEventDate) {
        return 0;
    }
    return localeCompareCollator.compare(firstEventDate, secondEventDate);
};

export const sortPlayersByAwayTeam = (
    firstPlayer: PlayerWithTeam & { isAway: boolean; eventStartTime: string },
    secondPlayer: PlayerWithTeam & { isAway: boolean; eventStartTime: string }
) => {
    return Number(secondPlayer.isAway) - Number(firstPlayer.isAway);
};

export const sortPlayersInProjectionView = (
    firstPlayer: PlayerWithTeam & { isAway: boolean; eventStartTime: string },
    secondPlayer: PlayerWithTeam & { isAway: boolean; eventStartTime: string },
    playerProjectionFilter: string
) => {
    const shouldSortAscending = playerProjectionFilter === 'STROKES';
    return (
        sortPlayersByPromoPicks(firstPlayer, secondPlayer, playerProjectionFilter) ||
        sortPlayersByEventStartDate(firstPlayer, secondPlayer) ||
        sortPlayersByProjectionValue(firstPlayer, secondPlayer, playerProjectionFilter, shouldSortAscending) ||
        sortPlayersByAwayTeam(firstPlayer, secondPlayer)
    );
};

export const sortPlayers = (
    firstPlayer: PlayerWithTeam,
    secondPlayer: PlayerWithTeam,
    positionFilters?: PositionFilters[]
) => {
    return (
        sortPlayersByPosition(firstPlayer, secondPlayer, positionFilters) ||
        sortPlayersByFantasyPoints(firstPlayer, secondPlayer) ||
        sortPlayersByName(firstPlayer, secondPlayer)
    );
};

export const sortPlayersByPromoPicks = (
    firstPlayer: PlayerWithTeam,
    secondPlayer: PlayerWithTeam,
    /**
     * If provided, only checks if players have the given projection as promo
     */
    playerProjectionFilter?: string
) => {
    const p1Projections = playerProjectionFilter
        ? firstPlayer?.projections.filter(it => it.key === playerProjectionFilter)
        : firstPlayer.projections;
    const p2Projections = playerProjectionFilter
        ? secondPlayer?.projections.filter(it => it.key === playerProjectionFilter)
        : secondPlayer.projections;
    const firstNonRegular = getNonRegularProjections(p1Projections);
    const secondNonRegular = getNonRegularProjections(p2Projections);
    return (
        Number(secondNonRegular?.hasDiscountedProjections) - Number(firstNonRegular?.hasDiscountedProjections) ||
        Number(secondNonRegular?.hasNukeProjections) - Number(firstNonRegular?.hasNukeProjections) ||
        Number(secondNonRegular?.hasAnchorProjections) - Number(firstNonRegular?.hasAnchorProjections)
    );
};

export const sortLobbyPlayers = (firstItem: PlayerWithTeamAndEvent, secondItem: PlayerWithTeamAndEvent) => {
    return (
        sortPlayersByPromoPicks(firstItem, secondItem) ||
        sortEventsByDate(firstItem.event, secondItem.event) ||
        sortPlayersByName(firstItem, secondItem)
    );
};

export const filterPlayers = (playersList: PlayerWithTeam[]) => {
    const uniquePlayers: string[] = [];
    return playersList.filter(element => {
        const key = element.firstName + element.lastName;
        const isDuplicate = uniquePlayers.includes(key);

        if (!isDuplicate) {
            uniquePlayers.push(key);
            return true;
        }

        return false;
    });
};
